"use client";

import { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { addToCart as dbAddToCart } from "@/actions/cart";
import type { StoreModel } from "@/store";
import { useStoreActions } from "easy-peasy";
import {
  ArrowUpRight,
  CheckCircle2,
  Heart,
  Loader2,
  LockKeyhole,
  Share2,
  ShoppingCart,
  Star,
} from "lucide-react";
import {
  AnimatePresence,
  motion,
  MotionConfig,
  useReducedMotion,
} from "motion/react";
import { toast } from "sonner";

import type { PricePlans } from "@/config/products";
import type { ProductMediaItem } from "@/lib/product-media";
import type { ProductDetails } from "@/lib/product-details";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import MaxWidthWrapper from "@/components/shared/max-width-wrapper";
import { NumberCounter } from "@/components/products/NumberCounter";
import { ProductEditorial } from "@/components/products/ProductEditorial";
import Testimonials from "@/components/sections/testimonials";
import type { ReviewItem } from "@/actions/reviews";

import styles from "./product-detail.module.css";
import { ProductShowcase } from "./ProductShowcase";

type MediaType = "image" | "gif" | "video";

export interface ProductDetailProps {
  productId: string;
  title: string;
  gallery?: ProductMediaItem[];
  mediaUrl: string;
  mediaType?: MediaType;
  tags?: string[];
  summary?: string;
  description?: string;
  details?: ProductDetails;
  reviews?: Array<{
    id: string;
    rating: number;
    displayName: string;
    helpertag: string;
    review: string;
    image: string | null;
    createdAt: Date;
    productId?: string | null;
    productName?: string | null;
  }>;
  allReviews?: ReviewItem[];
  relatedProducts?: Array<{
    id: string;
    name: string;
    category: string;
    gameTag: string;
    mediaUrl: string;
    price: number;
  }>;
  price?: number;
  pricePlans?: PricePlans;
  defaultPlan?: string;
}

const PLAN_LABELS: Record<string, string> = {
  day: "Day",
  week: "Week",
  month: "Month",
  lifetime: "Lifetime",
};

function getPlanLabel(key: string) {
  return PLAN_LABELS[key] ?? key.charAt(0).toUpperCase() + key.slice(1);
}

function getPlanGridClass(planCount: number) {
  if (planCount === 1) return "grid-cols-1";
  if (planCount === 2) return "grid-cols-2";
  if (planCount === 3) return "grid-cols-3";
  return "grid-cols-2 sm:grid-cols-4";
}

export default function ProductDetail({
  productId,
  title,
  mediaUrl,
  gallery = [],
  mediaType = "image",
  tags = [],
  summary,
  description,
  details = {},
  reviews = [],
  allReviews = [],
  relatedProducts = [],
  price = 0,
  pricePlans,
  defaultPlan,
}: ProductDetailProps) {
  const router = useRouter();
  const reduced = useReducedMotion();
  const [intro, featureText] = (summary ?? "").split(/Key features:\s*/i);
  const features =
    featureText
      ?.split("•")
      .map((item) => item.trim())
      .filter(Boolean) ?? [];
  const isVideo = mediaType === "video";
  const validPlans = useMemo(() => {
    const entries = Object.entries(pricePlans ?? {}).filter(([, value]) => {
      const numericValue = Number(value);
      return Number.isFinite(numericValue) && numericValue > 0;
    });

    return Object.fromEntries(
      entries.map(([key, value]) => [key, Number(value)]),
    );
  }, [pricePlans]);
  const planKeys = Object.keys(validPlans);
  const hasPlans = planKeys.length > 0;
  const resolvedDefault =
    defaultPlan && validPlans[defaultPlan] !== undefined
      ? defaultPlan
      : (planKeys[0] ?? "");
  const [selectedPlan, setSelectedPlan] = useState(resolvedDefault);
  const [quantity, setQuantity] = useState(1);
  const [atcState, setAtcState] = useState<"idle" | "loading" | "added">(
    "idle",
  );
  const [isFavorited, setIsFavorited] = useState(false);

  const hasReviews = reviews.length > 0;
  const reviewCount = reviews.length;
  const calculatedAverage = hasReviews
    ? (reviews.reduce((total, review) => total + review.rating, 0) / reviews.length).toFixed(1)
    : "5.0";
  const displayRating = hasReviews ? calculatedAverage : "5.0";
  const displayCount = reviewCount;

  const handleShare = async () => {
    const url = typeof window !== "undefined" ? window.location.href : "";
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({
          title: `${title} – Foulware`,
          url,
        });
        return;
      } catch (err) {
        // Fallback to clipboard if share cancelled or rejected
      }
    }
    if (typeof navigator !== "undefined" && navigator.clipboard) {
      await navigator.clipboard.writeText(url);
      toast.success("Product link copied to clipboard!");
    }
  };

  const handleToggleFavorite = () => {
    setIsFavorited((prev) => {
      const next = !prev;
      toast.success(next ? "Saved to wishlist" : "Removed from wishlist");
      return next;
    });
  };

  const scrollToReviews = (e: React.MouseEvent) => {
    e.preventDefault();
    const el =
      document.getElementById("reviews") ||
      document.getElementById("reviews-heading");
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const renderProductHeader = (isMobile = false) => (
    <div className={isMobile ? "mb-6 lg:hidden" : "hidden lg:block"}>
      <div className="flex items-center justify-between gap-3">
        <p className="font-mono text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">
          Official digital license
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            aria-label="Share product"
            onClick={handleShare}
            className="flex size-9 items-center justify-center rounded-lg border border-border/80 bg-card/60 text-muted-foreground transition-colors hover:border-border hover:bg-muted hover:text-foreground active:scale-95"
          >
            <Share2 className="size-4" />
          </button>
          <button
            type="button"
            aria-label={
              isFavorited ? "Remove from wishlist" : "Add to wishlist"
            }
            onClick={handleToggleFavorite}
            className={cn(
              "flex size-9 items-center justify-center rounded-lg border border-border/80 bg-card/60 transition-colors hover:border-border hover:bg-muted active:scale-95",
              isFavorited
                ? "border-rose-500/40 text-rose-500"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            <Heart
              className={cn("size-4", isFavorited && "fill-current")}
            />
          </button>
        </div>
      </div>

      <h1
        id={isMobile ? "mobile-product-title" : "product-title"}
        className={cn(styles.productTitle, "mt-2.5 sm:mt-3")}
      >
        {title}
      </h1>

      <div className="mt-3 flex flex-wrap items-center gap-1.5 sm:gap-2">
        <div className="flex items-center gap-0.5 text-amber-500">
          {[...Array(5)].map((_, i) => (
            <Star
              key={i}
              className="size-4 fill-amber-500 text-amber-500"
            />
          ))}
        </div>
        <span className="ml-1 font-bold text-foreground text-sm sm:text-base">
          {displayRating}
        </span>
        <span className="text-sm text-muted-foreground">
          ({displayCount} verified{" "}
          {displayCount === 1 ? "review" : "reviews"})
        </span>
        <span className="text-sm text-muted-foreground/60">•</span>
        <button
          type="button"
          onClick={scrollToReviews}
          className="text-sm font-medium text-foreground hover:underline cursor-pointer transition-colors focus:outline-none focus-visible:underline"
        >
          {hasReviews ? "Read reviews" : "Write a review"}
        </button>
      </div>
    </div>
  );

  const addToCart = useStoreActions(
    (actions: { cart: StoreModel["cart"] }) => actions.cart.addToCart,
  );
  const removeFromCart = useStoreActions(
    (actions: { cart: StoreModel["cart"] }) => actions.cart.removeFromCart,
  );

  useEffect(() => {
    if (hasPlans && validPlans[selectedPlan] === undefined) {
      setSelectedPlan(resolvedDefault);
    }
  }, [hasPlans, resolvedDefault, selectedPlan, validPlans]);

  useEffect(() => {
    setAtcState("idle");
  }, [quantity, selectedPlan]);

  const currentPrice = hasPlans
    ? (validPlans[selectedPlan] ?? validPlans[planKeys[0]] ?? 0)
    : price;

  function handleAddToCart() {
    if (atcState !== "idle") return;

    setAtcState("loading");

    const plan = hasPlans ? selectedPlan : undefined;
    addToCart({ productId, quantity, plan });
    dbAddToCart(productId, hasPlans ? selectedPlan : null, quantity).catch(
      () => undefined,
    );

    const selectedPlanLabel = hasPlans ? getPlanLabel(selectedPlan) : undefined;
    toast.success(
      selectedPlanLabel ? `${title} — ${selectedPlanLabel}` : title,
      {
        description: "Added to cart",
        duration: 4000,
        action: {
          label: "Undo",
          onClick: () => {
            removeFromCart({ productId, plan });
            setAtcState("idle");
          },
        },
      },
    );
    setAtcState("added");
  }

  return (
    <MotionConfig reducedMotion="user">
      <MaxWidthWrapper
        className={cn(
          styles.page,
          "py-8 pb-16 md:py-12 md:pb-24",
        )}
      >
        {renderProductHeader(true)}
        <motion.div
          className={styles.productGrid}
          initial={reduced ? false : { opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.38, ease: "easeOut" }}
        >
          <div className={styles.mediaColumn}>
            <ProductShowcase
              key={productId}
              gallery={gallery}
              title={title}
              mediaUrl={mediaUrl}
              isVideo={isVideo}
            />
          </div>

          <aside className={styles.purchaseColumn} aria-label={title}>
            <section className={styles.productIntro}>
              {renderProductHeader(false)}
              {intro && <p className={styles.summary}>{intro}</p>}

              {features.length > 0 && (
                <div className={styles.featureList}>
                  {features.map((feature) => (
                    <div key={feature} className={styles.feature}>
                      <CheckCircle2 size={14} />
                      {feature.replace(/\.$/, "")}
                    </div>
                  ))}
                </div>
              )}
            </section>
            <section
              className={styles.purchase}
              aria-label="Choose your license"
            >
              {hasPlans && (
                <fieldset className={styles.planFieldset}>
                  <div className={styles.planHeading}>
                    <legend>License duration</legend>
                    <span>Digital delivery</span>
                  </div>
                  <div
                    className={cn(
                      styles.planGrid,
                      getPlanGridClass(planKeys.length),
                    )}
                  >
                    {planKeys.map((key) => {
                      const isSelected = key === selectedPlan;
                      return (
                        <button
                          key={key}
                          type="button"
                          aria-pressed={isSelected}
                          onClick={() => setSelectedPlan(key)}
                          className={cn(
                            styles.planButton,
                            isSelected && styles.selectedPlan,
                          )}
                        >
                          {isSelected && (
                            <motion.span
                              layoutId="selected-product-plan"
                              className={styles.selectedPlanOutline}
                              transition={{
                                type: "spring",
                                stiffness: 420,
                                damping: 34,
                              }}
                            />
                          )}
                          <span>{getPlanLabel(key)}</span>
                          <strong>${validPlans[key]}</strong>
                          {key === defaultPlan && <em>Popular</em>}
                        </button>
                      );
                    })}
                  </div>
                </fieldset>
              )}
              <div className={styles.priceRow}>
                <div>
                  <p className={styles.priceLabel}>
                    {quantity > 1 ? "Total" : "License price"}
                  </p>
                  <div className={styles.priceValue}>
                    <span>${Number((currentPrice * quantity).toFixed(2))}</span>
                    {hasPlans && (
                      <small>/{getPlanLabel(selectedPlan).toLowerCase()}</small>
                    )}
                  </div>
                  <p className={styles.priceDetail}>
                    Includes software updates and support
                  </p>
                </div>
                <NumberCounter
                  value={quantity}
                  onChange={setQuantity}
                  min={1}
                  max={99}
                />
              </div>

              <motion.div
                whileHover={atcState === "loading" ? undefined : { y: -1 }}
                whileTap={atcState === "loading" ? undefined : { scale: 0.99 }}
                className={styles.cartAction}
              >
                <Button
                  type="button"
                  size="lg"
                  rounded="xl"
                  className={cn(
                    styles.cta,
                    atcState !== "added" && styles.cta,
                    atcState === "added" &&
                    "bg-emerald-600 text-white hover:bg-emerald-700",
                  )}
                  onClick={
                    atcState === "added"
                      ? () => router.push("/checkout")
                      : handleAddToCart
                  }
                  disabled={atcState === "loading"}
                >
                  <AnimatePresence mode="wait" initial={false}>
                    {atcState === "idle" && (
                      <motion.span
                        key="idle"
                        initial={{ opacity: 0, y: 4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4 }}
                        className="flex items-center gap-2"
                      >
                        <ShoppingCart data-icon="inline-start" />
                        Add to cart — $
                        {Number((currentPrice * quantity).toFixed(2))}{" "}
                        <ArrowUpRight className="ml-1 size-4" />
                      </motion.span>
                    )}
                    {atcState === "loading" && (
                      <motion.span
                        key="loading"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="flex items-center gap-2"
                      >
                        <Loader2
                          data-icon="inline-start"
                          className="animate-spin"
                        />
                        Adding to cart…
                      </motion.span>
                    )}
                    {atcState === "added" && (
                      <motion.span
                        key="added"
                        initial={{ opacity: 0, scale: 0.96 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0 }}
                        className="flex items-center gap-2"
                      >
                        <CheckCircle2 data-icon="inline-start" />
                        Go to cart
                      </motion.span>
                    )}
                  </AnimatePresence>
                </Button>
              </motion.div>
              <p className={styles.checkoutNote}>
                <LockKeyhole size={13} /> Protected checkout · Crypto, cards &
                balance accepted
              </p>
            </section>
          </aside>
        </motion.div>

        <ProductEditorial
          details={details}
          fallbackDescription={description}
        />
      </MaxWidthWrapper>

      <Testimonials
        id="reviews"
        className="scroll-mt-24"
        containerClassName="border-t border-border/70 pt-14 sm:pt-16"
        initialReviews={reviews as ReviewItem[]}
        productName={title}
        productId={productId}
      />
    </MotionConfig>
  );
}
